/**
 * Liste « Premiers pas » vivante (C21 SP4, 4.2) : l'état des six faits, lu du
 * serveur (une seule vérité, `GET /api/premiers-pas/etat`), et deux
 * préférences locales — « Masquer » (levée par l'aide « Premiers pas ») et
 * « la note Retours a été ouverte au moins une fois » (la ligne 6 se coche
 * à la première ouverture, le serveur ne peut pas le savoir).
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import { fetchPremiersPasEtat, type PremiersPasEtat } from '../../services/premiersPasAPI';
import { lignesPremiersPas, toutesFaites } from '../../components/help/premiersPas/progress';

export type { PremiersPasEtat };

export const MASQUE_KEY = 'anagogia.premiersPas.masque';
export const RETOURS_OUVERT_KEY = 'anagogia.premiersPas.retoursOuvert';

function lireDrapeau(cle: string): boolean {
  try { return window.localStorage.getItem(cle) === '1'; } catch { return false; }
}
function ecrireDrapeau(cle: string, valeur: boolean): void {
  try {
    if (valeur) window.localStorage.setItem(cle, '1'); else window.localStorage.removeItem(cle);
  } catch { /* stockage indisponible : la préférence ne survit pas au rechargement */ }
}

interface PremiersPasState {
  etat: PremiersPasEtat | null;
  status: string;
  masque: boolean;
  retoursOuvert: boolean;
  /**
   * « Revoir les premiers pas » demandé pendant la session (fumée § 7.7 du 22/10/2026) : une liste finie se
   * cache d'elle-même, sans passer par `masque` ; ce drapeau la remontre. Jamais persisté : au prochain
   * chargement, la liste finie se cache à nouveau.
   */
  revoir: boolean;
}

const initialState: PremiersPasState = {
  etat: null,
  status: LOADING_STATES.IDLE,
  masque: lireDrapeau(MASQUE_KEY),
  retoursOuvert: lireDrapeau(RETOURS_OUVERT_KEY),
  revoir: false,
};

export const fetchPremiersPasEtatAsync = createAsyncThunk<PremiersPasEtat, any>('premiersPas/fetch', async (instance) => {
  const token = await getTokenRedirect(tokenRequest, instance);
  if (!token) throw new Error('jeton indisponible');
  return fetchPremiersPasEtat(token);
});

const premiersPasSlice = createSlice({
  name: 'premiersPas',
  initialState,
  reducers: {
    masquerPremiersPas(state) { state.masque = true; state.revoir = false; ecrireDrapeau(MASQUE_KEY, true); },
    afficherPremiersPas(state) { state.masque = false; state.revoir = true; ecrireDrapeau(MASQUE_KEY, false); },
    marquerRetoursOuvert(state) { state.retoursOuvert = true; ecrireDrapeau(RETOURS_OUVERT_KEY, true); },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPremiersPasEtatAsync.pending, (state) => { state.status = LOADING_STATES.LOADING; })
      .addCase(fetchPremiersPasEtatAsync.fulfilled, (state, action) => { state.etat = action.payload; state.status = LOADING_STATES.SUCCEEDED; })
      .addCase(fetchPremiersPasEtatAsync.rejected, (state) => { state.status = LOADING_STATES.FAILED; });
  },
});

export const { masquerPremiersPas, afficherPremiersPas, marquerRetoursOuvert } = premiersPasSlice.actions;
export const selectPremiersPasEtat = (state: any): PremiersPasEtat | null => state.premiersPas?.etat ?? null;
export const selectPremiersPasStatus = (state: any): string => state.premiersPas?.status ?? LOADING_STATES.IDLE;
export const selectPremiersPasMasque = (state: any): boolean => state.premiersPas?.masque ?? false;
export const selectPremiersPasRevoir = (state: any): boolean => state.premiersPas?.revoir ?? false;
/**
 * « Revoir les premiers pas » a un sens quand la liste est masquée, ou finie et pas encore revue dans la
 * session (fumée § 7.7 du 22/10/2026 : une liste finie se cache d'elle-même, sans passer par « Masquer »).
 */
export const selectPremiersPasRevoyable = (state: any): boolean => {
  const tranche: PremiersPasState | undefined = state.premiersPas;
  if (!tranche) return false;
  if (tranche.masque) return true;
  if (!tranche.etat || tranche.revoir) return false;
  return toutesFaites(lignesPremiersPas(tranche.etat, { retoursOuvert: tranche.retoursOuvert, mobile: false }));
};
export const selectRetoursOuvert = (state: any): boolean => state.premiersPas?.retoursOuvert ?? false;
export default premiersPasSlice.reducer;
